/**
 * Junction FieldTypes — depth-2 references.
 *
 * A junction IS a reference, composed twice. The stored id resolves into a
 * junction table this field owns (`record_links`); the row it lands on carries
 * an inner `reference` to the real entity (`linked → records`). References all
 * the way down — the presence of an inner `reference` is what marks a junction
 * (see `isJunctionField`). So these are `parent: "reference"`, not a root of
 * their own:
 *
 *   link       → record_links,       inner ref record { linked: records }
 *   tagging    → record_taggings,    inner ref tag    { tag: tags }
 *   attachment → record_attachments, inner ref asset  { asset: assets }
 *
 * Each entry spreads `REFERENCE` so it's fully flat; a junction that groups adds
 * `...group.byTarget`. Data only: operator groups live in `../operators/junction.ts`.
 */

import * as group from "../group.js"
import * as sort from "../sort.js"
import type { ReferenceFieldType } from "../types.js"
import { linkableTypeNames } from "./linkableTypeNames.js"
import { fieldTypes as REFERENCES } from "./reference.js"

// Junction type names — references carrying an inner `reference` to the target
// entity, plus the attachment subtypes. A distinct union because the component
// and operator registries key on it.
export type JunctionType = "link" | "backlink" | "tagging" | "attachment"
export type AttachmentType = "image" | "audio" | "video" | "document"

// The reference root each junction spreads, plus the target entities its inner
// `reference` points at (record/tag/asset, reached via their own `relationship`
// hop).
const REFERENCE = REFERENCES.reference
const RECORD = REFERENCES.record
const TAG = REFERENCES.tag
const ASSET = REFERENCES.asset

// Display goes through the target entity reached via the inner
// `reference.relationship`; storage is just a junction-row id — no `format` /
// `parse` here.

// Keyed by the literal `JunctionType` union so every junction type must be
// present and `fieldTypes.attachment` is a known property the subtype map spreads.
export const fieldTypes: Record<JunctionType, ReferenceFieldType> = {
  // LINK — scoped to a record type (person, deal, organization, …) via the
  // field's `recordType`, sourced from the entity-type registry. The inner
  // `reference` is a `record` reached via `linked`.
  link: {
    ...REFERENCE,
    name: "link",
    label: "Link",
    parent: "reference",
    icon: "link",
    table: "record_links",
    relationship: "links",
    reference: { ...RECORD, relationship: "linked", column: "linked_id" },
    sort: sort.text,
    ...group.byTarget,
    recordTypes: () => linkableTypeNames(),
  },

  // BACKLINK — the same `record_links` row read from the target side. `links`
  // joins on `record_id` (rows where I'm the source) and resolves the `linked`
  // record; `backlinks` joins on `linked_id` (rows where I'm the target) and
  // resolves the `record` (the source that links to me). So a backlink is a
  // `link` with the two hop strings swapped: relation `backlinks`, inner ref hop
  // `record`. Read-only — an incoming link is authored from the source side, so
  // backlink fields carry `isEditable: false` and render as read-only chips.
  backlink: {
    ...REFERENCE,
    name: "backlink",
    label: "Backlink",
    parent: "reference",
    icon: "arrows-left-right",
    table: "record_links",
    relationship: "backlinks",
    // same entity (record), different hop — `record` (source) not `linked`,
    // whose FK on the shared record_links row is `record_id`.
    reference: { ...RECORD, relationship: "record", column: "record_id" },
    ...group.byTarget,
    isEditable: false,
  },

  // TAGGING — record_taggings junction; inner ref is a `tag` reached via `tag`.
  tagging: {
    ...REFERENCE,
    name: "tagging",
    label: "Tagging",
    parent: "reference",
    icon: "tag",
    table: "record_taggings",
    relationship: "taggings",
    reference: { ...TAG, relationship: "tag", column: "tag_id" },
    sort: sort.text,
    ...group.byTarget,
  },

  // ATTACHMENT + subtypes. record_attachments junction; inner ref is an `asset`
  // reached via `asset`. The image/document/video leaves spread `attachment` so
  // they inherit table + inner ref, then override identity.
  attachment: {
    ...REFERENCE,
    name: "attachment",
    label: "Attachment",
    parent: "reference",
    icon: "paperclip",
    table: "record_attachments",
    relationship: "attachments",
    reference: { ...ASSET, relationship: "asset", column: "asset_id" },
  },
}

// ATTACHMENT subtypes — spread `fieldTypes.attachment` to inherit table + inner
// reference, then override identity. Each holds one kind of file (`accepts`);
// `document` is every file that isn't an image, audio or video.
export const attachmentTypes: Record<AttachmentType, ReferenceFieldType> = {
  image: {
    ...fieldTypes.attachment,
    name: "image",
    label: "Image",
    parent: "attachment",
    icon: "image",
    accepts: "image",
  },
  audio: {
    ...fieldTypes.attachment,
    name: "audio",
    label: "Audio",
    parent: "attachment",
    icon: "file-audio",
    accepts: "audio",
  },
  video: {
    ...fieldTypes.attachment,
    name: "video",
    label: "Video",
    parent: "attachment",
    icon: "file-video",
    accepts: "video",
  },
  document: {
    ...fieldTypes.attachment,
    name: "document",
    label: "Document",
    parent: "attachment",
    icon: "file-alt",
    accepts: "document",
  },
}

Object.assign(fieldTypes, attachmentTypes)
